import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppBar } from "../components/Layout";
import { api } from "../lib/api";

const slotText = (b) => (Array.isArray(b.slots) ? b.slots.join(", ") : b.slots);

function Filters({ selectedDate, setSelectedDate, searchPhone, setSearchPhone }) {
  return (
    <div className="vv-filters">
      <label className="vv-field">
        <span className="vv-field__label">Date</span>
        <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="vv-input vv-input--date" />
      </label>
      <label className="vv-field">
        <span className="vv-field__label">Phone</span>
        <input type="text" inputMode="numeric" value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)} placeholder="Search by phone number" className="vv-input" />
      </label>
      <button type="button" onClick={() => { setSelectedDate(""); setSearchPhone(""); }} className="vv-btn vv-btn--outline">
        Clear filters
      </button>
    </div>
  );
}

export function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const navigate = useNavigate();

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedDate) params.date = selectedDate;
      if (searchPhone) params.phone = searchPhone;
      const result = await api("admin", { params });
      setBookings(result.bookings || []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookings([]);
      // The session cookie is the gate, so an expired one means re-login.
      if (error.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, searchPhone]);

  const handleLogout = async () => {
    try {
      await api("session", { method: "DELETE" });
    } catch (e) {
      /* clearing the cookie is best-effort */
    }
    navigate("/login");
  };

  const deleteBooking = async (id) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      try {
        await api("admin", { method: "DELETE", params: { id } });
        fetchBookings();
        alert("Booking deleted successfully!");
      } catch (error) {
        console.error("Error deleting booking:", error);
        if (error.status === 401) { navigate("/login"); return; }
        alert(error.message || "Failed to delete booking");
      }
    }
  };

  const revenue = bookings.reduce((sum, b) => sum + (b.total_price || 0), 0);

  return (
    <div className="vv-page vv-page--paper">
      <AppBar title="Bookings dashboard">
        <Link to="/" className="vv-appbar__link">Website</Link>
        <button type="button" onClick={handleLogout} className="vv-btn vv-btn--ghost vv-btn--sm">Log out</button>
      </AppBar>
      <main className="vv-wrap vv-dash">
        <Filters {...{ selectedDate, setSelectedDate, searchPhone, setSearchPhone }} />

        {loading ? (
          <p className="vv-muted">Loading bookings…</p>
        ) : bookings.length === 0 ? (
          <div className="vv-panel vv-empty">No bookings found for the selected criteria.</div>
        ) : (
          <>
            <div className="vv-stats">
              <div className="vv-stat"><span>Total bookings</span><strong className="vv-display">{bookings.length}</strong></div>
              <div className="vv-stat"><span>Total revenue</span><strong className="vv-display">₹{revenue}</strong></div>
            </div>
            <div className="vv-panel vv-tablewrap">
              <table className="vv-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Name</th><th>Phone</th><th>Payment</th><th>Block</th><th>Slots</th><th>Total</th><th>Promo</th><th><span className="vv-sr">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id}>
                      <td className="vv-nowrap">{b.booking_date}</td>
                      <td>{b.name}</td>
                      <td className="vv-nowrap">{b.phone}</td>
                      <td>{b.payment_mode || "None"}</td>
                      <td className="vv-cap">{b.time_block}</td>
                      <td className="vv-table__slots">{slotText(b)}</td>
                      <td className="vv-table__price">₹{b.total_price}</td>
                      <td>{b.promo_code || "None"}</td>
                      <td><button type="button" onClick={() => deleteBooking(b.id)} className="vv-btn vv-btn--danger vv-btn--sm">Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export function StaffBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [searchPhone, setSearchPhone] = useState("");

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        // Scoped to a single day and gated on a staff session.
        const result = await api("staff", { params: selectedDate ? { date: selectedDate } : {} });
        const rows = result.bookings || [];
        const needle = searchPhone.trim();
        setBookings(needle ? rows.filter((b) => String(b.phone || "").includes(needle)) : rows);
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [selectedDate, searchPhone]);

  return (
    <div className="vv-page vv-page--paper">
      <AppBar title="Today at the court">
        <Link to="/" className="vv-appbar__link">Website</Link>
      </AppBar>
      <main className="vv-wrap vv-dash">
        <Filters {...{ selectedDate, setSelectedDate, searchPhone, setSearchPhone }} />
        {loading ? (
          <p className="vv-muted">Loading bookings…</p>
        ) : bookings.length === 0 ? (
          <div className="vv-panel vv-empty">No bookings found for the selected criteria.</div>
        ) : (
          <div className="vv-panel vv-tablewrap">
            <table className="vv-table">
              <thead>
                <tr><th>Date</th><th>Slots</th><th>Name</th><th>Phone</th><th>Total</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id}>
                    <td className="vv-nowrap">{b.booking_date}</td>
                    <td className="vv-table__slots">{slotText(b)}</td>
                    <td>{b.name}</td>
                    <td className="vv-nowrap">{b.phone}</td>
                    <td className="vv-table__price">₹{b.total_price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
